<template>
    <!-- Show skeleton loading when loading products or when we have IDs but no products yet -->
    <div v-if="(loading || (ids?.length > 0 && products.length === 0)) && !loadingMoreProducts" :class="classes">
        <ShopwareProductSkeleton v-for="index in ids?.length || 48" :key="`skeleton-${index}`" />
    </div>
    <!-- Show products when loaded -->
    <div v-else-if="products.length > 0" :class="classes">
        <ShopwareProduct
            v-for="(product, index) in products"
            :product="product"
            :addToHistory="!search"
            :key="product?.id"
            @close-search="emit('closeSearch')"
            :requestId="requestId"
            :size
            :isAboveFold="index < 6" />
    </div>
    <!-- Show no results message -->
    <div v-else-if="!loading && !loadingMoreProducts && products.length === 0 && ids?.length === 0" class="w-full">
        <p>Geen zoekresultaten gevonden</p>
    </div>
</template>

<script setup lang="ts">
    import { useApiClient } from '~/src/apiClient';

    const props = defineProps<{
        classes?: object | string;
        ids: Array<any>;
        loadingMoreProducts?: boolean;
        loading?: boolean;
        /** Search UI (e.g. header popup): GTM uses `view_search_results` instead of `view_item_list`. */
        search?: boolean;
        /** Use with `search` for GTM `search_term`. */
        searchTerm?: string;
        size?: string;
        requestId?: string;
        cachedProducts?: Array<any>; // Optional cached product data
    }>();
    const emit = defineEmits(['closeSearch']);
    const products: Ref<any[]> = ref([]);
    const totalResults: Ref<number | null> = ref(null);
    const { trackProductList, trackViewSearchResults } = useGTMEcommerce();
    const loading = ref(false);

    const { apiClient } = useApiClient();
    const productIds: Ref<Array<any>> = ref([]);

    // Access product cache store
    const productCacheStore = useProductCacheStore();

    async function loadProductsById() {
        productIds.value = Object.values(props.ids);
        if (productIds.value.length === 0) {
            products.value = [];
            loading.value = false;
            return;
        }

        // Set loading state if not using cache
        loading.value = true;

        // First, check if we have cached products passed from parent
        if (props.cachedProducts && props.cachedProducts.length > 0) {
            // Use cached products to match against current product IDs
            const orderedProducts = productIds.value
                .map(id => props.cachedProducts!.find(product => product.id === id))
                .filter(Boolean); // Remove any undefined products

            if (orderedProducts.length === productIds.value.length) {
                products.value = orderedProducts;
                loading.value = false;
                viewItemListEvent(orderedProducts);
                return orderedProducts;
            }
        }

        // If no cached products or incomplete cache, check the global cache store
        const currentActualProducts = productCacheStore.getCurrentActualProducts();
        if (currentActualProducts && currentActualProducts.length > 0) {
            const orderedProducts = productIds.value
                .map(id => currentActualProducts.find(product => product.id === id))
                .filter(Boolean); // Remove any undefined products

            if (orderedProducts.length === productIds.value.length) {
                products.value = orderedProducts;
                loading.value = false;
                viewItemListEvent(orderedProducts);
                return orderedProducts;
            }
        }

        // If no cache available or incomplete, make API call
        const productsResponse = await apiClient.invoke('readProduct post /product', {
            body: {
                filter: [
                    {
                        type: 'equalsAny',
                        field: 'id',
                        value: productIds.value.join('|'),
                    },
                    {
                        type: 'equals',
                        field: 'active',
                        value: true,
                    },
                ],
                sort: [],
                // Ensure Shopware calculates total count
                'total-count-mode': 'exact',
                associations: {
                    // For gallery images used on hover
                    media: {
                        associations: {
                            media: {},
                        },
                    },
                    cover: {
                        associations: {
                            media: {},
                        },
                    },
                    // For brand name under the product
                    manufacturer: {},
                },
            },
            headers: {
                'sw-include-seo-urls': 'true',
            } as any,
        });

        let productsOrdering = productsResponse.data.elements;
        // Keep ordering by ids, but never keep `undefined` entries (they cause duplicate/undefined v-for keys and unstable patching)
        let orderedProducts = productIds.value
            .map(id => productsOrdering.find(product => product.id === id))
            .filter(Boolean);

        products.value = orderedProducts;
        totalResults.value =
            (productsResponse?.data as any)?.total ??
            (productsResponse?.data as any)?.meta?.total ??
            orderedProducts.length ??
            null;
        loading.value = false;
        viewItemListEvent(orderedProducts);

        return orderedProducts;
    }

    function viewItemListEvent(items: any[]) {
        const withCover = items.filter((p: any) => p?.coverId != null);
        if (props.search && props.searchTerm) {
            trackViewSearchResults(props.searchTerm, withCover.length ? withCover : items);
            return;
        }
        trackProductList(items, 'product_listing', 'Product Listing');
    }

    // Sync loading prop with internal loading state
    watch(
        () => props.loading,
        newValue => {
            if (newValue !== undefined) {
                loading.value = newValue;
            }
        },
        { immediate: true },
    );

    // Initialize loading state when we have IDs but no products
    watch(
        () => props.ids,
        newIds => {
            if (newIds && newIds.length > 0 && products.value.length === 0) {
                loading.value = true;
            }
        },
        { immediate: true },
    );

    // Load products immediately on mount and when ids change
    onMounted(() => {
        if (props.ids && props.ids.length > 0) {
            loading.value = true;
            loadProductsById();
        }
    });

    watch(
        () => props.ids,
        value => {
            if (value && value.length > 0) {
                loading.value = true;
                loadProductsById();
            } else {
                loading.value = false;
                products.value = [];
            }
        },
        { deep: true },
    );
</script>
